import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Loader2 } from 'lucide-react'
import type { Laboratorio, LaboratorioCreate, LaboratorioUpdate, GrupoOption } from '@/modules/multichannel/types/configuracion'

interface LaboratorioFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  laboratorio: Laboratorio | null
  grupos: GrupoOption[]
  onSubmit: (data: LaboratorioCreate) => void
  onUpdate: (id: number, data: LaboratorioUpdate) => void
  isSubmitting: boolean
}

interface FormData {
  grupo_id: string
  nombre: string
  descripcion: string
}

export function LaboratorioFormDialog({
  open, onOpenChange, laboratorio, grupos, onSubmit, onUpdate, isSubmitting,
}: LaboratorioFormDialogProps) {
  const isEdit = !!laboratorio

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<FormData>({
    defaultValues: { grupo_id: '', nombre: '', descripcion: '' },
  })

  useEffect(() => {
    if (open && laboratorio) {
      reset({
        grupo_id: String(laboratorio.grupo_id),
        nombre: laboratorio.nombre,
        descripcion: laboratorio.descripcion || '',
      })
    } else if (!open) {
      reset({ grupo_id: '', nombre: '', descripcion: '' })
    }
  }, [open, laboratorio, reset])

  const handleGrupoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const grupoId = e.target.value
    setValue('grupo_id', grupoId)
    if (!isEdit) {
      const grupo = grupos.find(g => String(g.grupo_id) === grupoId)
      if (grupo) setValue('nombre', grupo.grupo_nombre)
    }
  }

  const onFormSubmit = (data: FormData) => {
    if (isEdit && laboratorio) {
      onUpdate(laboratorio.id, {
        nombre: data.nombre,
        descripcion: data.descripcion || undefined,
      })
    } else {
      onSubmit({
        grupo_id: Number(data.grupo_id),
        nombre: data.nombre,
        descripcion: data.descripcion || undefined,
        activo: true,
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar Laboratorio' : 'Nuevo Laboratorio'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Modifica los datos del laboratorio.' : 'Selecciona un grupo y asigna un nombre al laboratorio.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="grupo_id">Grupo <span className="text-destructive">*</span></Label>
            <select
              id="grupo_id"
              {...register('grupo_id', { required: 'Selecciona un grupo' })}
              onChange={handleGrupoChange}
              disabled={isEdit || isSubmitting}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">Seleccionar grupo...</option>
              {grupos.map(g => (
                <option key={g.grupo_id} value={g.grupo_id}>{g.grupo_nombre}</option>
              ))}
            </select>
            {errors.grupo_id && <p className="text-sm text-destructive">{errors.grupo_id.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="nombre">Nombre <span className="text-destructive">*</span></Label>
            <Input
              id="nombre"
              {...register('nombre', { required: 'El nombre es requerido' })}
              disabled={isSubmitting}
            />
            {errors.nombre && <p className="text-sm text-destructive">{errors.nombre.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripcion</Label>
            <Input id="descripcion" {...register('descripcion')} disabled={isSubmitting} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
